import mixpanel from 'mixpanel-browser'
import { get } from 'lodash'
import metadata from '@/metadata.json'
import btbConf from '@/btb-conf.json'
import store from '@/store';
import { TrackProperties } from '@/analytics/events/BTBA';
import AccountInfo, { AccountIdentity } from '@/types/AccountInfo';

type PeapleOptions = {
  'Company Name': string;
  'Company Id': string;
  'From Modstore': boolean;
  'Account Domain Name': string;
}

export default class Analytics {
  public PROJECT_KEY: string | undefined = undefined
  public API_HOST: string | undefined = undefined
  private userId: string = ""
  private companyName: string = ""
  private companyId: string = ""

  init(accountInfo: AccountInfo) {
    if(!btbConf.finalBuild) return
    // Mixpanel test Project, will be used if not overridden bellow
    this.PROJECT_KEY = process.env.VUE_APP_MIXPANEL_PROJECT_TOKEN_DEV!
    this.API_HOST = process.env.VUE_APP_MIXPANEL_HOST_DEV!

    if (this.IsFromExternalLMSAndIsNotOD(accountInfo)) {
      // External LMS, JSONFile will be used
      this.PROJECT_KEY = process.env.VUE_APP_MIXPANEL_EXTERNAL_PROJECT_TOKEN_PROD!
      this.API_HOST = process.env.VUE_APP_MIXPANEL_EXTERNAL_HOST_PROD!
    } else if (this.IsNotOD(accountInfo)) {
      // Modstore, KB4.info is available and will be used
      this.PROJECT_KEY = process.env.VUE_APP_MIXPANEL_PROJECT_TOKEN_PROD!
      this.API_HOST = process.env.VUE_APP_MIXPANEL_HOST_PROD!
    }

    if (this.PROJECT_KEY && this.API_HOST) {
      mixpanel.init(this.PROJECT_KEY, { api_host: this.API_HOST, disable_persistence: true }, '')
    }
  }

  IsFromExternalLMSAndIsNotOD(accountInfo: AccountInfo){
    return accountInfo && accountInfo.external_lms && !accountInfo.is_od
  }
  IsNotOD(accountInfo: AccountInfo){
    return accountInfo && !accountInfo.is_od
  }

  trackEvent(event: string, extraParameters?: TrackProperties) {
    if (this.allowAnalytics()) {
      mixpanel.track(event, {
        ...extraParameters,
        ...this.requiredProperties(),
      })
    }
  }
  
  setAccountIdentity(accountIdentity: AccountIdentity) {
    accountIdentity.user_id = accountIdentity.from_modstore ?  accountIdentity.user_id : accountIdentity.account.id
    this.identify(accountIdentity.user_id);
    this.people(accountIdentity.user_id, {
      'Company Name': accountIdentity.account.name,
      'Company Id': accountIdentity.account.id,
      'From Modstore': accountIdentity.from_modstore,
      'Account Domain Name': accountIdentity.account.domain_name
    });
  }

  identify(identifyId: string) {
    this.userId = identifyId
    if (this.allowAnalytics()) {
      mixpanel.identify(identifyId)
    }
  }

  public people(emailId: string, properties: PeapleOptions) {
    if (this.allowAnalytics()) {
      this.companyName = properties['Company Name']
      this.companyId = properties['Company Id']
      mixpanel.people.set({
        $email: emailId,
        ...properties,
      })
    }
  }

  allowAnalytics() {
    return !!(this.PROJECT_KEY &&
      this.API_HOST &&
      !metadata.hasOwnProperty('__developFlag') && // __developFlag is present only on the default player metadata.
      this.userId
    )
  }

  getUserId(){
    return this.userId
  }

  requiredProperties() {
    const { innerWidth: width, innerHeight: height } = window;
    return {
      // Account Information
      'Account ID': this.companyId,
      'Account Name': this.companyName,
      'Browser Language': `${window.navigator.language}`.toLowerCase(),
      'Viewport Width': width,
      'Viewport Height': height,
      'Training User ID': this.userId,

      // Metadata
      'Training ID': `${get(metadata, 'id')}:${get(btbConf, 'courseInfo.version')}`,
      'Title': get(metadata, 'name'),
      'Publisher': get(metadata, 'publisher'),
      'Training Year': get(metadata, 'revision_year'),
      'Training Mode': get(metadata, 'navigation_mode'),
      'Training Estimated Duration': get(metadata, 'duration'),
      'Training Type': get(metadata, 'type'),
      'Training Tracking Mode': get(btbConf, 'skipMode'),
      'Training Score Format': get(metadata, 'scoring_format'),
      'Training Score Result Format': store.getters['theme/scormResultFormat'] || get(metadata, 'scorm_result_format'),

      'Training Series': get(metadata, 'series'),
      'Training Season': get(metadata, 'season'),
      'Training Episode': get(metadata, 'episode'),
      'Training Keywords': get(metadata, 'keywords'),
      'Training Subject': get(metadata, 'subject_category'),
      'Training Customization': get(metadata, 'custom_module'),

      // Training Settings
      'Training Passing Score': get(btbConf, 'passingScore'),
      'Training Scorm Build': get(btbConf, 'scormBuildVersion', 'unknown'),
      'Training Language': `${get(btbConf, 'courseInfo.localization')}`.toLowerCase(),

      // Versions
      'Builder Version': get(btbConf, 'builderInfo.version'),
      'Player Version': get(btbConf, 'playerInfo.version'),
      'Course Version': get(btbConf, 'courseInfo.version'),
      'Training Version': store.getters['app/getVersion']
    }
  }
}